/*
 * MedexOVA motion primitives — shared building blocks for the "next-level" UX.
 *
 * These are used by RiderApp, DriverTripApp, DriverBeacon, and AdminHome to
 * layer consistent feedback on every state change. See /app/memory/ARCHITECTURE.md
 * §Motion vocabulary for the design vocabulary.
 */
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";

// -----------------------------------------------------------------------------
// AnimatedNumber — counts up (or down) with a spring. Used for fare quotes,
// KPI cards, ETA counters, live fare meters.
// -----------------------------------------------------------------------------
export function AnimatedNumber({ value, prefix = "", suffix = "", format = (v) => v.toFixed(2), duration = 500, className, testid }) {
  const [display, setDisplay] = useState(value);
  const startRef = useRef(value);
  useEffect(() => {
    const start = display;
    startRef.current = start;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      // ease-out-cubic
      const eased = 1 - Math.pow(1 - p, 3);
      const cur = start + (value - start) * eased;
      setDisplay(cur);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span data-testid={testid} className={className}>{prefix}{format(display)}{suffix}</span>;
}

// -----------------------------------------------------------------------------
// WaitRing — circular progress ring, green → amber → red as timer ticks.
// Used by both Rider and Driver PWAs during ARRIVED_PICKUP state, synced from server.
// -----------------------------------------------------------------------------
export function WaitRing({ startedAt, size = 120, greenSec = 180, amberSec = 300 }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, []);
  const started = startedAt ? new Date(startedAt).getTime() : now;
  const elapsedSec = Math.max(0, Math.floor((now - started) / 1000));
  const total = amberSec + 60; // no-show trigger
  const progress = Math.min(1, elapsedSec / total);
  const color = elapsedSec < greenSec ? "#2B4C3B" : elapsedSec < amberSec ? "#B45309" : "#B91C1C";
  const r = (size - 12) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * (1 - progress);
  const mm = Math.floor(elapsedSec / 60).toString().padStart(2, "0");
  const ss = (elapsedSec % 60).toString().padStart(2, "0");
  return (
    <svg width={size} height={size} data-testid="wait-ring">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E2E0D8" strokeWidth={6} />
      <motion.circle
        cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={6}
        strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={dash}
        transform={`rotate(-90 ${size/2} ${size/2})`}
        animate={{ stroke: color }} transition={{ duration: 0.4 }}
      />
      <text x={size/2} y={size/2 - 4} textAnchor="middle" fontSize={20} fontWeight={800} fill={color} fontFamily="monospace">{mm}:{ss}</text>
      <text x={size/2} y={size/2 + 14} textAnchor="middle" fontSize={9} fill="#475569" fontWeight={700} letterSpacing="0.14em">WAITING</text>
    </svg>
  );
}

// -----------------------------------------------------------------------------
// DrainRing — a countdown ring that empties from 100% → 0% over `seconds`.
// Wraps the driver's "Accept" button during a 20-second incoming hail.
// -----------------------------------------------------------------------------
export function DrainRing({ seconds = 20, size = 84, onExpire, color = "#2B4C3B", children }) {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => {
    const t0 = Date.now();
    const iv = setInterval(() => {
      const elapsed = (Date.now() - t0) / 1000;
      const left = Math.max(0, seconds - elapsed);
      setRemaining(left);
      if (left <= 0) { clearInterval(iv); onExpire && onExpire(); }
    }, 60);
    return () => clearInterval(iv);
  }, [seconds, onExpire]);
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * (1 - remaining / seconds);
  const displayColor = remaining < seconds * 0.3 ? "#B91C1C" : color;
  return (
    <div style={{ position: "relative", width: size, height: size, display: "grid", placeItems: "center" }} data-testid="drain-ring">
      <svg width={size} height={size} style={{ position: "absolute", inset: 0 }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E2E0D8" strokeWidth={4} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={displayColor} strokeWidth={4}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={dash}
          transform={`rotate(-90 ${size/2} ${size/2})`} style={{ transition: "stroke 0.3s" }} />
      </svg>
      {children}
    </div>
  );
}

// -----------------------------------------------------------------------------
// SonarPing — repeating ripple pulse around a marker.
// Used on the driver's "Go Online" pin + on the rider's origin ping while
// searching for drivers.
// -----------------------------------------------------------------------------
export function SonarPing({ color = "#2B4C3B", size = 40 }) {
  return (
    <div style={{ position: "relative", width: size, height: size, display: "grid", placeItems: "center" }} aria-hidden data-testid="sonar-ping">
      {[0, 1, 2].map(i => (
        <motion.span key={i}
          style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${color}` }}
          initial={{ scale: 0.3, opacity: 0.6 }}
          animate={{ scale: 2.2, opacity: 0 }}
          transition={{ duration: 2, repeat: Infinity, delay: i * 0.66, ease: "easeOut" }}
        />
      ))}
      <div style={{ width: size * 0.4, height: size * 0.4, borderRadius: "50%", background: color, boxShadow: `0 0 0 4px ${color}22` }} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// SlideDownCard — the driver's incoming-hail card slides in from the top.
// -----------------------------------------------------------------------------
export function SlideDownCard({ open, children }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: "-100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ x: "120%", opacity: 0, transition: { duration: 0.28 } }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9500 }}
          data-testid="slide-down-hail"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// -----------------------------------------------------------------------------
// haptic — best-effort short vibration + optional visual flash callback.
// -----------------------------------------------------------------------------
export function haptic(pattern = 40) {
  try { if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(pattern); } catch { /* silent */ }
}

// -----------------------------------------------------------------------------
// fireConfetti — 800ms celebration burst on trip completion.
// -----------------------------------------------------------------------------
export function fireConfetti() {
  try {
    confetti({ particleCount: 90, spread: 70, origin: { y: 0.6 }, colors: ["#2B4C3B", "#D4A017", "#3A7BD5", "#F9F8F6"] });
    setTimeout(() => confetti({ particleCount: 60, spread: 100, origin: { y: 0.5, x: 0.2 } }), 250);
    setTimeout(() => confetti({ particleCount: 60, spread: 100, origin: { y: 0.5, x: 0.8 } }), 400);
  } catch { /* canvas not supported */ }
}

// -----------------------------------------------------------------------------
// SpringyStars — rating widget with a bouncy scale on each tap.
// -----------------------------------------------------------------------------
export function SpringyStars({ value = 0, onChange, size = 36 }) {
  return (
    <div style={{ display: "flex", gap: 4, justifyContent: "center" }} data-testid="springy-stars">
      {[1, 2, 3, 4, 5].map(n => (
        <motion.button
          key={n}
          onClick={() => onChange && onChange(n)}
          whileTap={{ scale: 1.35 }}
          transition={{ type: "spring", stiffness: 500, damping: 15 }}
          style={{
            background: "transparent", border: "none", cursor: "pointer",
            fontSize: size, lineHeight: 1, padding: 4,
            color: n <= value ? "#D4A017" : "#E2E0D8",
          }}
          data-testid={`star-${n}`}
          aria-label={`Rate ${n} of 5`}
        >★</motion.button>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// AmberFlash — quick amber overlay pulse when the driver is approaching.
// -----------------------------------------------------------------------------
export function AmberFlash({ trigger }) {
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (!trigger) return;
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 260);
    return () => clearTimeout(t);
  }, [trigger]);
  return (
    <AnimatePresence>
      {flash && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 0.6 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.26 }}
          style={{ position: "fixed", inset: 0, background: "#B45309", pointerEvents: "none", zIndex: 9800, mixBlendMode: "screen" }}
          data-testid="amber-flash"
        />
      )}
    </AnimatePresence>
  );
}
